import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { MagneticWrap } from "./MagneticWrap";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Work", to: "/work" },
  { label: "Insights", to: "/insights" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const linkCls = (to) =>
    `text-[13px] font-medium uppercase tracking-[0.14em] transition-colors duration-300 ${
      pathname === to ? "text-white" : "text-[#A6A6B0] hover:text-white"
    }`;

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
          scrolled
            ? "bg-[#050507]/80 backdrop-blur-xl border-white/[0.06]"
            : "bg-transparent border-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="focus:outline-none" aria-label="BrandZenith home">
            <Logo />
          </Link>

          <div className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={linkCls(l.to)}
              >
                {l.label}
              </Link>
            ))}
          </div>

          <div className="hidden lg:block">
            <MagneticWrap strength={0.2}>
              <Link
                to="/contact"
                data-testid="nav-cta"
                className="group flex items-center gap-2 bg-[#6D00FF] hover:bg-[#8B2CFF] text-white text-[13px] font-semibold uppercase tracking-[0.12em] px-5 py-2.5 rounded-md transition-[background-color,box-shadow] duration-300 hover:shadow-[0_0_28px_-6px_rgba(109,0,255,0.7)]"
              >
                Request a Proposal
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </MagneticWrap>
          </div>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(true)}
            className="lg:hidden text-white p-2"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] bg-[#050507]/95 backdrop-blur-2xl flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="h-[72px] px-6 flex items-center justify-between">
              <Logo />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="text-white p-2"
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    className={`block font-display text-4xl font-extrabold uppercase tracking-tight py-3 border-b border-white/[0.06] ${
                      pathname === l.to ? "text-gradient-purple" : "text-white/80"
                    }`}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <Link
                  to="/contact"
                  data-testid="mobile-nav-cta"
                  className="mt-8 flex items-center justify-center gap-2 bg-[#6D00FF] text-white font-semibold uppercase tracking-[0.12em] text-sm px-6 py-4 rounded-md"
                >
                  Request a Proposal <ArrowUpRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
